import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Cpu,
  Sparkles,
  MessageSquare,
  Route as RouteIcon,
  Radio,
  ScanLine,
  Smartphone,
} from "lucide-react";

/*
 * TtkHero · combined title + system architecture.
 *
 * A radial ecosystem diagram: the Twin pulses at the centre, three tools
 * orbit around it (App / Habit Tiles / TriKarma Band), and the four things
 * the app does branch out from the App node as an outer ring.
 *
 * Left column carries the "TRIFIT.LIFE · One system. One twin." title copy.
 * Right column carries the radial diagram. Full-viewport, animates on mount.
 */

const TOOLS = [
  {
    id: "app",
    name: "Trifit Activity Suite",
    kind: "APP",
    Icon: Smartphone,
    // Position on the orbit (angle in degrees, 0 = 3 o'clock, 90 = 12 o'clock)
    angle: 90,
  },
  {
    id: "band",
    name: "TriKarma Band",
    kind: "HARDWARE",
    Icon: Radio,
    angle: 0,
  },
  {
    id: "tiles",
    name: "Habit Tiles",
    kind: "HARDWARE",
    Icon: ScanLine,
    angle: 180,
  },
];

const APP_CAPABILITIES = [
  { id: "twin", name: "The Twin", Icon: Cpu },
  { id: "simulator", name: "The Simulator", Icon: Sparkles },
  { id: "coach", name: "Adaptive Coaching", Icon: MessageSquare },
  { id: "routine", name: "Your Custom Routine", Icon: RouteIcon },
];

// Convert polar (angle in degrees, radius in %) → cartesian on a 0..100 SVG
const polar = (angle, radius) => {
  const rad = (angle * Math.PI) / 180;
  return {
    x: 50 + radius * Math.cos(rad),
    y: 50 - radius * Math.sin(rad), // SVG y is inverted
  };
};

/* -------------------------------------------------------------------- */
/*  The Twin pulsing hub · phone screenshot at the centre                  */
/* -------------------------------------------------------------------- */
const TwinHub = () => (
  <g>
    {/* Outer glow rings */}
    {[0, 0.6, 1.2].map((delay) => (
      <motion.circle
        key={delay}
        cx="50"
        cy="50"
        r="15"
        fill="none"
        stroke="#D2378B"
        strokeWidth="0.25"
        vectorEffect="non-scaling-stroke"
        initial={{ scale: 1, opacity: 0.5 }}
        animate={{ scale: [1, 1.8, 1.8], opacity: [0.55, 0, 0] }}
        transition={{
          duration: 2.4,
          delay,
          repeat: Infinity,
          ease: "easeOut",
        }}
        style={{ transformOrigin: "50% 50%" }}
      />
    ))}

    {/* Clip path for a rounded phone-shaped centre. Phone is 24 wide × 46 tall
        (viewBox units) centred on 50,50 → spans 38..62 × 27..73. */}
    <defs>
      <clipPath id="twin-phone-clip">
        <rect x="38" y="27" width="24" height="46" rx="3.6" ry="3.6" />
      </clipPath>
    </defs>

    {/* Phone body outline */}
    <rect
      x="38"
      y="27"
      width="24"
      height="46"
      rx="3.6"
      ry="3.6"
      fill="#0A0408"
      stroke="#D2378B"
      strokeWidth="0.5"
      vectorEffect="non-scaling-stroke"
    />

    {/* Screenshot */}
    <image
      href="/assets/app/app_home_twin.webp"
      x="38"
      y="27"
      width="24"
      height="46"
      preserveAspectRatio="xMidYMid slice"
      clipPath="url(#twin-phone-clip)"
    />

    <text
      x="50"
      y="78"
      textAnchor="middle"
      className="fill-white font-mono"
      style={{ fontSize: 1.8, letterSpacing: "0.18em" }}
    >
      THE TWIN
    </text>
  </g>
);

/* -------------------------------------------------------------------- */
/*  Data stream · moving dot along a straight radial line                 */
/* -------------------------------------------------------------------- */
const DataStream = ({ from, to, delay = 0, duration = 2 }) => (
  <motion.circle
    r="0.6"
    fill="#D2378B"
    initial={{ cx: from.x, cy: from.y, opacity: 0 }}
    animate={{
      cx: [from.x, to.x],
      cy: [from.y, to.y],
      opacity: [0, 1, 1, 0],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      repeatDelay: 1.5,
      ease: "easeInOut",
    }}
  />
);

/* -------------------------------------------------------------------- */
/*  Tool node with icon                                                   */
/* -------------------------------------------------------------------- */
const ToolNode = ({ tool, position, delay = 0 }) => (
  <motion.g
    initial={{ opacity: 0, scale: 0.7 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    style={{ transformOrigin: `${position.x}% ${position.y}%` }}
  >
    {/* Outer halo */}
    <circle
      cx={position.x}
      cy={position.y}
      r="6.5"
      fill="#0C060A"
      stroke="#D2378B"
      strokeOpacity="0.35"
      strokeWidth="0.25"
      vectorEffect="non-scaling-stroke"
    />
    {/* Node face */}
    <circle
      cx={position.x}
      cy={position.y}
      r="4.5"
      fill="#0C060A"
      stroke="#D2378B"
      strokeWidth="0.4"
      vectorEffect="non-scaling-stroke"
    />
    <foreignObject
      x={position.x - 2}
      y={position.y - 2}
      width="4"
      height="4"
    >
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        className="flex h-full w-full items-center justify-center text-signal"
      >
        <tool.Icon
          style={{ width: "100%", height: "100%" }}
          strokeWidth={1.8}
        />
      </div>
    </foreignObject>
  </motion.g>
);

/* -------------------------------------------------------------------- */
/*  Radial diagram                                                        */
/* -------------------------------------------------------------------- */
const RadialSystem = () => {
  const orbitRadius = 34; // % from centre
  const outerRadius = 48; // outer capabilities ring

  // Position each tool on the orbit
  const toolPositions = TOOLS.map((t) => ({
    ...t,
    pos: polar(t.angle, orbitRadius),
  }));
  const appTool = toolPositions.find((t) => t.id === "app");

  // Distribute 4 capabilities on an arc AROUND the App tool at the top,
  // tightened to 40°..140° so the outer nodes clear the taller centre phone.
  const capArcStart = 40;
  const capArcEnd = 140;
  const capPositions = APP_CAPABILITIES.map((c, i) => {
    const t = i / (APP_CAPABILITIES.length - 1);
    const angle = capArcStart + t * (capArcEnd - capArcStart);
    return { ...c, pos: polar(angle, outerRadius) };
  });

  return (
    <svg
      viewBox="0 0 100 100"
      className="h-full w-full"
      role="img"
      aria-label="Trifit.life ecosystem"
    >
      <defs>
        <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D2378B" stopOpacity="0.35" />
          <stop offset="60%" stopColor="#D2378B" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#D2378B" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Ambient hub glow behind everything */}
      <circle cx="50" cy="50" r="42" fill="url(#hubGlow)" />

      {/* Rotating dashed orbit ring */}
      <motion.circle
        cx="50"
        cy="50"
        r={orbitRadius}
        fill="none"
        stroke="#D2378B"
        strokeOpacity="0.35"
        strokeWidth="0.3"
        strokeDasharray="0.6 1.4"
        vectorEffect="non-scaling-stroke"
        animate={{ rotate: 360 }}
        transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: "50% 50%" }}
      />

      {/* Static outer orbit ring · very faint */}
      <circle
        cx="50"
        cy="50"
        r={outerRadius}
        fill="none"
        stroke="#D2378B"
        strokeOpacity="0.14"
        strokeWidth="0.25"
        strokeDasharray="0.4 1.8"
        vectorEffect="non-scaling-stroke"
      />

      {/* Cross reticle */}
      <line x1="50" y1="10" x2="50" y2="14" stroke="#D2378B" strokeOpacity="0.4" strokeWidth="0.25" vectorEffect="non-scaling-stroke" />
      <line x1="50" y1="86" x2="50" y2="90" stroke="#D2378B" strokeOpacity="0.4" strokeWidth="0.25" vectorEffect="non-scaling-stroke" />
      <line x1="10" y1="50" x2="14" y2="50" stroke="#D2378B" strokeOpacity="0.4" strokeWidth="0.25" vectorEffect="non-scaling-stroke" />
      <line x1="86" y1="50" x2="90" y2="50" stroke="#D2378B" strokeOpacity="0.4" strokeWidth="0.25" vectorEffect="non-scaling-stroke" />

      {/* Spokes · center to each tool node */}
      {toolPositions.map((t, i) => (
        <motion.line
          key={`spoke-${t.id}`}
          x1="50"
          y1="50"
          x2={t.pos.x}
          y2={t.pos.y}
          stroke="#D2378B"
          strokeOpacity="0.55"
          strokeWidth="0.35"
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.9, delay: 0.15 + i * 0.15, ease: "easeOut" }}
        />
      ))}

      {/* Sub-spokes · App node to each of the 4 capabilities */}
      {capPositions.map((c, i) => (
        <motion.line
          key={`sub-${c.id}`}
          x1={appTool.pos.x}
          y1={appTool.pos.y}
          x2={c.pos.x}
          y2={c.pos.y}
          stroke="#D2378B"
          strokeOpacity="0.35"
          strokeWidth="0.25"
          vectorEffect="non-scaling-stroke"
          strokeDasharray="0.6 0.6"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.9, delay: 1.1 + i * 0.1, ease: "easeOut" }}
        />
      ))}

      {/* Data streams flowing from tools INTO the Twin */}
      {toolPositions.map((t, i) => (
        <DataStream
          key={`stream-${t.id}`}
          from={t.pos}
          to={{ x: 50, y: 50 }}
          delay={1.6 + i * 0.6}
          duration={1.8}
        />
      ))}

      {/* Central hub */}
      <TwinHub />

      {/* Tool nodes */}
      {toolPositions.map((t, i) => (
        <ToolNode
          key={t.id}
          tool={t}
          position={t.pos}
          delay={0.4 + i * 0.15}
        />
      ))}

      {/* Capability outer nodes (smaller) */}
      {capPositions.map((c, i) => (
        <motion.g
          key={c.id}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 1.3 + i * 0.1 }}
          style={{ transformOrigin: `${c.pos.x}% ${c.pos.y}%` }}
        >
          <circle
            cx={c.pos.x}
            cy={c.pos.y}
            r="3"
            fill="#0C060A"
            stroke="#D2378B"
            strokeOpacity="0.65"
            strokeWidth="0.3"
            vectorEffect="non-scaling-stroke"
          />
          <foreignObject
            x={c.pos.x - 1.3}
            y={c.pos.y - 1.3}
            width="2.6"
            height="2.6"
          >
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              className="flex h-full w-full items-center justify-center text-signal"
            >
              <c.Icon
                style={{ width: "100%", height: "100%" }}
                strokeWidth={1.9}
              />
            </div>
          </foreignObject>
        </motion.g>
      ))}
    </svg>
  );
};

/* -------------------------------------------------------------------- */
/*  HTML overlay labels · positioned absolutely so type stays crisp      */
/* -------------------------------------------------------------------- */
const NodeLabel = ({ pos, name, kind, delay, anchor = "center" }) => {
  // Anchor controls both text alignment AND translate so the label grows
  // away from the diagram edge · fixes overflow on the far-right/far-left tools.
  const align =
    anchor === "left"
      ? { textAlign: "left", transform: "translate(0, -50%)" }
      : anchor === "right"
      ? { textAlign: "right", transform: "translate(-100%, -50%)" }
      : { textAlign: "center", transform: "translate(-50%, -50%)" };
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      className="pointer-events-none absolute max-w-[110px] md:max-w-none"
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, ...align }}
    >
      <p className="font-display text-xs font-light leading-tight text-white md:whitespace-nowrap md:text-base">
        {name}
      </p>
      {kind && (
        <p className="mt-1 whitespace-nowrap font-mono text-[8px] tracking-[0.22em] text-signal">
          {kind}
        </p>
      )}
    </motion.div>
  );
};

const CapabilityLabel = ({ pos, name, delay, anchor = "center" }) => {
  const align =
    anchor === "left"
      ? { textAlign: "left", transform: "translate(0, -50%)" }
      : anchor === "right"
      ? { textAlign: "right", transform: "translate(-100%, -50%)" }
      : { textAlign: "center", transform: "translate(-50%, -50%)" };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, delay }}
      className="pointer-events-none absolute max-w-[84px] md:max-w-none"
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, ...align }}
    >
      <p className="font-mono text-[9px] leading-tight tracking-[0.18em] text-white/85 md:whitespace-nowrap">
        {name}
      </p>
    </motion.div>
  );
};

/* -------------------------------------------------------------------- */
/*  Section                                                               */
/* -------------------------------------------------------------------- */
const TtkHero = () => {
  // Recompute positions once for label overlay
  const orbitRadius = 34;
  const outerRadius = 48;
  const labelPadRadial = 10; // push labels a bit beyond the node

  const toolLabels = TOOLS.map((t) => {
    const pos = polar(t.angle, orbitRadius + labelPadRadial);
    // Anchor the label so it grows AWAY from the diagram edge
    let anchor = "center";
    if (t.angle > 60 && t.angle < 120) anchor = "center"; // top · centred
    else if (t.angle >= 120 && t.angle <= 240) anchor = "right"; // left side
    else anchor = "left"; // right side
    return { id: t.id, name: t.name, kind: t.kind, pos, anchor };
  });

  const capLabels = APP_CAPABILITIES.map((c, i) => {
    const t = i / (APP_CAPABILITIES.length - 1);
    const angle = 40 + t * 100;
    // Push labels slightly outside the outer ring
    const pos = polar(angle, outerRadius + 6);
    // Left half caps → right-anchored, right half → left-anchored
    let anchor = "center";
    if (angle < 75) anchor = "left";
    else if (angle > 105) anchor = "right";
    return { id: c.id, name: c.name, pos, anchor };
  });

  return (
    <section
      data-testid="ttk-hero-section"
      id="ttk-system"
      className="relative flex min-h-screen items-center overflow-hidden px-6 py-24 md:px-12 md:py-20"
    >
      <div className="mx-auto grid w-full max-w-[1600px] items-center gap-12 lg:grid-cols-12 lg:gap-8">
        {/* Left · title copy */}
        <div className="lg:col-span-4">
          <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
            TRIFIT.LIFE · 01 · THE SYSTEM
          </p>
          <h1
            data-testid="ttk-hero-headline"
            className="mt-6 font-display text-4xl font-light leading-[1.05] text-white sm:text-5xl lg:text-6xl"
          >
            One system.
            <br />
            One <span className="text-signal">twin</span>.
          </h1>
          <a
            href="#ttk-app-simulator-section"
            data-testid="ttk-hero-cta"
            className="group mt-10 inline-flex w-fit items-center gap-3 font-mono text-[11px] tracking-[0.2em] text-white transition-colors duration-300 hover:text-signal"
          >
            EXPLORE THE SYSTEM
            <span className="transition-transform duration-300 group-hover:translate-y-1">
              ↓
            </span>
          </a>
        </div>

        {/* Right · radial diagram */}
        <div className="lg:col-span-8">
          <div
            data-testid="ttk-hero-diagram"
            className="relative mx-auto aspect-square w-full max-w-[260px] sm:max-w-[420px] md:max-w-[540px] lg:max-w-[720px]"
          >
            <RadialSystem />

            {/* Tool labels (positioned absolutely for crisp type) */}
            {toolLabels.map((t, i) => (
              <NodeLabel
                key={t.id}
                pos={t.pos}
                name={t.name}
                kind={t.kind}
                anchor={t.anchor}
                delay={0.6 + i * 0.15}
              />
            ))}

            {/* Capability labels (arcs beyond outer ring) */}
            {capLabels.map((c, i) => (
              <CapabilityLabel
                key={c.id}
                pos={c.pos}
                name={c.name}
                anchor={c.anchor}
                delay={1.4 + i * 0.1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TtkHero;
